import React, { useState, useEffect } from "react";
import "../../styles/Global.css";
import styles from "../../pages/Login.module.css";
import InputField from "./InputField";
import { Link } from "react-router-dom";

interface ForgotPasswordFormProps {
  onSubmit?: (email: string) => void;
  isLoading?: boolean;
  success?: boolean;
  message?: string | null;
}

/**
 * Form for password reset functionality
 * Uses glassmorphism styling for a modern, translucent appearance
 *
 * @param {ForgotPasswordFormProps} props - Component properties
 * @returns {JSX.Element} - Rendered forgot password form component
 */
const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  isLoading = false,
  success = false,
  message = null,
}) => {
  // Form state
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update isSubmitted based on success prop
  useEffect(() => {
    if (success) {
      setIsSubmitted(true);
    }
  }, [success]);

  /**
   * Validates the email input
   * @returns {boolean} - Whether the email is valid
   */
  const validateEmail = (): boolean => {
    if (!email.trim()) {
      setError("Email is required");
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    setError("");
    return true;
  };

  /**
   * Handles form submission
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateEmail() && onSubmit && !isLoading) {
      onSubmit(email);
      // Don't set isSubmitted here - let it be controlled by the success prop
    }
  };

  return (
    <div className={styles.forgotPasswordForm}>
      <h2 className={styles.forgotPasswordTitle}>
        Reset Password
      </h2>

      <p className={styles.forgotPasswordSubtitle}>
        Enter your email and we'll send you instructions to reset your password
      </p>

      {/* Display external error message if not success but message exists */}
      {!success && message && (
        <div className={styles.errorMessage}>
          {message}
        </div>
      )}

      {!isSubmitted ? (
        <form onSubmit={handleSubmit}>
          <InputField
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
          />

          <button
            type="submit"
            disabled={isLoading}
            className={styles.submitButton}
          >
            {isLoading ? "SENDING..." : "SEND RESET LINK"}
          </button>
        </form>
      ) : (
        <div className={styles.successContainer}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles.successIcon}
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
              fill="currentColor"
            />
          </svg>

          <h3 className={styles.successHeading}>
            Email Sent!
          </h3>

          <p className={styles.successMessage}>
            {success && message
              ? message
              : "Check your inbox for instructions to reset your password."}
          </p>

          <Link
            to="/login"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={styles.successBackLink}
          >
            Back to Login
          </Link>
        </div>
      )}

      {!isSubmitted && (
        <div className={styles.backToLoginContainer}>
          <Link
            to="/login"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={styles.backToLoginLink}
          >
            Back to Login
          </Link>
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
